import React, { useContext, useMemo, useState } from "react";

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FormItem from "app/components/inputs/FormItem";
import TimeRangeInput from "app/components/inputs/TimeRangeInput";
import DateSelector from "./DateSelector";
import SessionTypeSelector from "./SessionTypeSelector";
import RecurrenceSelector from "./RecurrenceSelector";
import SchedulingUserSelector from "app/components/SchedulingUserSelector";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

import { errorMessageFor } from "./helpers";

import styles from "./styles.module.scss";

import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import { FacilityModel } from "app/models/scheduling/FacilityModel";
import { uniqBy } from "lodash";
import { flattenFamilyStudentProfiles } from "app/utils/familyStudentProfiles";
import PaymentSelector from "app/components/PaymentSelector";
import TextInput from "app/components/inputs/TextInput";
import { EventModel } from "app/models/EventModel";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";

import { useSessionTiming } from "./useSessionTiming";
import NotifyStudentsToggle from "./NotifyStudentsToggle";
import { useSessionPayment } from "./useSessionPayment";
import PaymentFields from "./PaymentFields";
import AutoGrowTextarea from "./AutoGrowTextarea";
import useCardOnFileStatus from "./useCardOnFileStatus";
import ResourceSelect from "./ResourceSelect";
import {
  ResourceChange,
  canBookCustomSession,
  nextChoice,
  resourceMenu,
  showResourceRow,
} from "./resourceChoice";
import useFacilityResources from "app/routes/Calendar/views/FacilityCalendarView/hooks/useFacilityResources";
import { activeBaysInAdminOrder } from "app/utils/request/scheduling/facilityResources";

import ContactIcon from "../../../../public/images/contact-icon.svg";
import AccountCircleIcon from "../../../../public/images/account-circle-icon.svg";
import ClockIcon from "../../../../public/images/clock-ico.svg";
import CalendarIcon from "../../../../public/images/calendar-ico.svg";
import DollarIcon from "../../../../public/images/dollar-ico.svg";
import EditPenIcon from "../../../../public/images/edit-pen.svg";
import LocationIcon from "../../../../public/images/location-dark.svg";
import LockIcon from "../../../../public/images/lock.svg";

type PropsDef = {
  lessonRateOptions: LessonRateModel[];
  onDateChange: (_date: Date) => void;
  coachProfile: CoachProfileModel;
  facility?: FacilityModel;
  canAddNewAthletes?: boolean;
  coachId?: string;
  facilityId?: string;
  availablePackages?: any[];
  setAvailablePackages?: React.Dispatch<React.SetStateAction<any[]>>;
  isEdit?: boolean;
  event?: EventModel;
  facilityBookingEnabled?: boolean;
  showCoachSelection?: boolean;
  coachProfileOptions?: CoachProfileModel[];
  onCoachChange?: (_e: any) => void;
  loadCoaches?: (_inputValue: string, _currentOptions: [], _additional: { page: number }) => Promise<any>;
  validateCoachSelection?: (_selection: CoachProfileModel[], _formValues: any) => boolean;
  watchEventType?: string;
};

export default function SessionForm(props: PropsDef) {
  const { t } = useTranslation();
  const {
    lessonRateOptions,
    onDateChange,
    coachProfile,
    facility,
    canAddNewAthletes,
    coachId,
    facilityId,
    availablePackages,
    setAvailablePackages,
    isEdit,
    event,
    showCoachSelection,
    onCoachChange,
    loadCoaches,
    validateCoachSelection,
    watchEventType,
  } = props;

  const {
    control,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext();

  const watchTimeZone = watch("timeZone");
  const watchStartTime = watch("start");
  const watchEndTime = watch("end");
  const watchLessonRateId = watch("lessonRateId");
  const watchRepeat = watch("repeat");
  const watchCoaches: CoachProfileModel[] = watch("coachProfiles");
  const watchNotes: string = watch("notes");
  const watchPrivateNotes: string = watch("privateNotes");
  // CN-2808: the private note stays collapsed behind "Add private note"
  // until the coach asks for it, or the booking already has one.
  const [privateNoteOpened, setPrivateNoteOpened] = useState(false);
  const showPrivateNote = privateNoteOpened || !!watchPrivateNotes?.trim();
  const watchStudents = watch("studentProfiles");
  const selectedAthletes = watch("studentProfiles") || [];
  const watchScheduleResourceId = watch("scheduleResourceId");

  const { luxonStartAt, dateList, onStartTimeChange, onEndTimeChange } = useSessionTiming({
    watchTimeZone,
    watchStartTime,
    watchEndTime,
    watchLessonRateId,
    lessonRateOptions,
  });

  const {
    watchPaymentMethod,
    watchPayingAthlete,
    paymentEditable,
    isCheckout,
    stripeEnabled,
    sessionDefaultPaymentMethod,
    shouldShowPayingAthlete,
    clearPaymentFields,
    isLoadingPackages,
  } = useSessionPayment({
    lessonRateOptions,
    coachProfile,
    selectedAthletes,
    availablePackages,
    setAvailablePackages,
    isEdit,
    event,
    watchLessonRateId,
    facility,
  });

  // Decide whether to offer "Charge card on file" as a payment method for
  // the booking by checking the paying athlete's saved-card status at the
  // coach's connected Stripe account. This is independent of the
  // facility-level booking options feature flag.
  //
  // The form's `paying_athlete_id` field is only required when the booking
  // has more than one athlete (see `shouldShowPayingAthlete`). For
  // single-athlete bookings — and for capacity-mode group bookings — derive
  // the implicit payer from the participant list, mirroring the package
  // lookup in useSessionPayment.
  const explicitPayingAthleteId =
    typeof watchPayingAthlete === "string"
      ? watchPayingAthlete
      : watchPayingAthlete?.id;
  const implicitPayingAthleteId =
    !explicitPayingAthleteId && selectedAthletes.length > 0
      ? selectedAthletes[0]?.id
      : undefined;
  const payingAthleteId = explicitPayingAthleteId || implicitPayingAthleteId;
  const cardOnFileStatus = useCardOnFileStatus(
    payingAthleteId,
    coachProfile?.externalId,
  );

  const user = useContext(UserContext);
  const allowCustomSession = canBookCustomSession(user, watchCoaches?.[0]?.id);

  const resourceManagementEnabled =
    facility?.featureFlags?.resourceManagement === true;
  const { resources: facilityResources } = useFacilityResources(
    facility?.id,
    resourceManagementEnabled,
  );
  const activeBays = useMemo(
    () => activeBaysInAdminOrder(facilityResources),
    [facilityResources],
  );
  const resourceContext = {
    lessonRates: lessonRateOptions,
    bays: activeBays,
    canBookCustom: allowCustomSession,
    // The Session Type select is off for a lesson with a fixed payment.
    optionLocked: isEdit && !paymentEditable,
  };
  const menu = resourceMenu(
    {
      lessonRateId: watchLessonRateId ?? null,
      scheduleResourceId: watchScheduleResourceId ?? null,
    },
    resourceContext,
  );

  // The bay the lesson holds, when the list does not offer it: an inactive
  // bay. The select shows its title from the lesson.
  const heldBay =
    watchScheduleResourceId != null &&
    !menu.bays.some((bay) => bay.id === watchScheduleResourceId)
      ? event?.scheduleResources?.find(
          (resource) => resource.id === watchScheduleResourceId,
        ) ?? null
      : null;
  const showResourceSelect = showResourceRow(
    resourceManagementEnabled,
    menu.bays,
    heldBay,
  );

  // Applies the rules of the bay to the option and the bay of the form.
  const applyResourceChange = (change: ResourceChange) => {
    const current = {
      lessonRateId: getValues("lessonRateId") ?? null,
      scheduleResourceId: getValues("scheduleResourceId") ?? null,
    };
    const next = nextChoice(current, change, resourceContext);

    if (next.scheduleResourceId !== current.scheduleResourceId) {
      setValue("scheduleResourceId", next.scheduleResourceId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    // A bay outside the option makes the lesson custom. The price and the
    // times stay, as when the user changes the price of an option.
    if (next.lessonRateId !== current.lessonRateId) {
      setValue("lessonRateId", next.lessonRateId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const onResourceSelect = (scheduleResourceId: number | null) => {
    setValue("scheduleResourcePicked", true);
    applyResourceChange({ kind: "bay", scheduleResourceId });
  };

  const onLessonRateChange = (changeEvent: any) => {
    const sessionType = changeEvent.target.value;
    setValue("lessonRateId", sessionType.id, { shouldValidate: true });
    applyResourceChange({ kind: "rate", lessonRateId: sessionType.id ?? null });
    setValue("rate", sessionType.rate);
    setValue("location", sessionType.location);

    if (!sessionType.id) {
      clearPaymentFields();
      if (sessionType.onlineDefault && stripeEnabled) {
        setValue("payment_method", "online", { shouldValidate: true });
      } else {
        setValue("payment_method", "offline", { shouldValidate: true });
      }
    } else {
      const tempDefault = sessionType.onlineDefault && stripeEnabled ? "online" : "offline";
      setValue("payment_method", tempDefault, { shouldValidate: true });
      setValue("selected_package_id", null, { shouldValidate: true });
    }

    // Use the backend-parsed minute count, not parseInt(duration): a free-form
    // duration like "2 hours" would parse to 2, computing a 2-minute session.
    const durationInMinutes = sessionType?.durationInMinutes;
    if (
      luxonStartAt.isValid &&
      typeof durationInMinutes === "number" &&
      durationInMinutes > 0
    ) {
      const newEndTime = luxonStartAt.plus({ minutes: durationInMinutes });
      setValue("end", newEndTime.toISO());
    } else {
      setValue("end", "");
    }
  };

  const onRateChange = (changeEvent: any) => {
    const newRate = changeEvent.target.value;
    setValue("rate", newRate);

    if (!watchLessonRateId) {
      return;
    }

    const sessionType = lessonRateOptions.find(
      (lr) => lr.id === watchLessonRateId,
    );

    if (!sessionType) {
      return;
    }

    if (sessionType.rate !== newRate) {
      setValue("lessonRateId", null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const sessionType = lessonRateOptions.find(
    (lr) => lr.id === watchLessonRateId,
  );
  const allowRecurring =
    user.isAdmin ||
    (user.role === RoleType.COACH &&
      watchCoaches &&
      watchCoaches[0]?.id === user.id);

  let minEventDate = null;
  let maxEventDate = null;

  if (
    !allowCustomSession &&
    watchCoaches[0]?.earliestBookingDate &&
    watchCoaches[0]?.latestBookingDate
  ) {
    minEventDate = watchCoaches[0].earliestBookingDate.toJSDate();
    maxEventDate = watchCoaches[0].latestBookingDate.toJSDate();
  }

  const loadStudents = async (
    inputValue: string,
    _currentOptions: [],
    additional: { page: number },
    newStudents: [],
  ) => {
    const exceptIds =
      watchStudents.value?.map((s: StudentProfileModel) => s.id) || [];
    const page = additional?.page || 1;

    // CN-1323 (CFTB-3281): nest managed children under their head
    // (options[include][]=children) when browsing AND searching — the BE expands
    // a matched child back to its parent + siblings, so families never split.
    const query = inputValue
      ? { query: inputValue, per_page: 20, page, except: exceptIds, options: { include: ["children"] } }
      : { per_page: 20, page, except: exceptIds, options: { include: ["children"] } };

    try {
      const response = await schedulingFetchGet(
        `/facilities/${facility.id}/student_profiles`,
        query,
        { withHeaders: true },
      );

      const { students, headers } = response;
      const [current_page, total_pages] = [
        Number(headers.get("Current-Page")),
        Number(headers.get("Total-Pages")),
      ];
      const next_page = current_page < total_pages ? current_page + 1 : null;

      const allStudents = uniqBy(
        [...newStudents, ...flattenFamilyStudentProfiles(students)],
        "id",
      );

      return {
        options: allStudents || [],
        hasMore: current_page < total_pages,
        additional: {
          page: next_page,
        },
      };
    } catch (error) {
      console.error("Error fetching data:", error);
      return {
        options: [],
        hasMore: false,
        additional: {
          page: null,
        },
      };
    }
  };

  return (
    <div className={styles.two_column_layout}>
      {/* Coach selector (FacilityCalendar only) */}
      {showCoachSelection && (
        <div className={styles.icon_row}>
          <img src={ContactIcon} alt="" className={styles.row_icon} />
          <div className={styles.row_content}>
            <FormItem
              error={errorMessageFor(errors, "coachProfiles")}
              className={styles.input_field}
            >
              <label className={styles.sr_only}>{t("Coach")}</label>
              <SchedulingUserSelector
                {...register("coachProfiles", {
                  onChange: onCoachChange,
                  validate: validateCoachSelection,
                })}
                control={control}
                loadOptions={loadCoaches}
                placeholder={t("Select Coach")}
                multiSelect={watchEventType === "coach_schedule_block"}
                allowMultiSelectAll
                allOptionsLabel={t("All Coaches")}
                canAddNewAthletes={false}
              />
            </FormItem>
          </div>
        </div>
      )}

      {/* Athletes row */}
      <div className={styles.icon_row}>
        <img src={AccountCircleIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <FormItem
            error={errorMessageFor(errors, "studentProfiles")}
            className={styles.input_field}
          >
            <label className={styles.sr_only}>{t("Participating Athletes")}</label>
            <SchedulingUserSelector
              {...register("studentProfiles", {
                validate: (ids) => ids.length > 0,
              })}
              name="studentProfiles"
              control={control}
              rules={{
                validate: (v) =>
                  (Array.isArray(v) && v.length >= 1) ||
                  t("Select at least one athlete"),
              }}
              loadOptions={loadStudents}
              placeholder={t("Select Athletes")}
              multiSelect
              canAddNewAthletes={canAddNewAthletes}
              coachId={coachId}
              facilityId={facilityId}
            />
          </FormItem>
        </div>
      </div>

      {/* Session Type row */}
      <div className={styles.icon_row}>
        <img src={ClockIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <FormItem className={styles.input_field}>
            <label className={styles.sr_only}>{t("Session Type")}</label>
            <SessionTypeSelector
              key={watchCoaches[0]?.id}
              {...register("lessonRateId", {
                onChange: onLessonRateChange,
                required: !allowCustomSession,
              })}
              control={control}
              options={lessonRateOptions}
              allowCustomSession={allowCustomSession}
              disabled={isEdit && !paymentEditable}
            />
          </FormItem>
        </div>
      </div>

      {/* Resource row */}
      {showResourceSelect && (
        <div className={styles.icon_row}>
          <img src={LocationIcon} alt="" className={styles.row_icon} />
          <div className={styles.row_content}>
            <FormItem
              error={errorMessageFor(errors, "scheduleResourceId")}
              className={styles.input_field}
            >
              <label className={styles.sr_only}>{t("Resource")}</label>
              <ResourceSelect
                name="scheduleResourceId"
                emptyChoice={menu.emptyChoice}
                bays={menu.bays}
                heldBay={heldBay}
                onSelect={onResourceSelect}
              />
            </FormItem>
          </div>
        </div>
      )}

      {/* Date / Time / Recurrence / Location row */}
      <div className={styles.icon_row}>
        <img src={CalendarIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <div className={styles.date_time_row}>
            <FormItem
              className={styles.input_field}
              error={errorMessageFor(errors, "date")}
            >
              <label className={styles.sr_only}>{t("Date")}</label>
              <DateSelector
                {...register("date", {
                  onChange: onDateChange,
                  required: t("Date is required"),
                  validate: (v) =>
                    (v instanceof Date && !isNaN(v.getTime())) ||
                    (v && typeof v.isValid === "boolean" && v.isValid) ||
                    t("Invalid date"),
                })}
                control={control}
                minDate={minEventDate}
                maxDate={maxEventDate}
                data-testid="date-picker-input"
              />
            </FormItem>

            <div className={styles.time_group}>
              <FormItem
                error={errorMessageFor(errors, "start")}
                className={styles.input_field}
              >
                <label className={styles.sr_only}>{t("Start Time")}</label>
                <TimeRangeInput
                  {...register("start", { onChange: onStartTimeChange })}
                  control={control}
                  timeZone={watchTimeZone}
                  dateList={dateList}
                  customStartTime={null}
                  placeholder={t("Start")}
                  data-testid="time-select-start"
                />
              </FormItem>

              <div className={styles.time_separator} />

              {allowCustomSession && (
                <FormItem
                  error={errorMessageFor(errors, "end")}
                  className={styles.input_field}
                >
                  <label className={styles.sr_only}>{t("End Time")}</label>
                  <TimeRangeInput
                    {...register("end", { onChange: onEndTimeChange })}
                    control={control}
                    timeZone={watchTimeZone}
                    dateList={dateList}
                    customStartTime={watchStartTime}
                    placeholder={t("End")}
                    data-testid="time-select-end"
                  />
                </FormItem>
              )}

              {!allowCustomSession && (
                <FormItem className={styles.input_field}>
                  <TextInput
                    value={sessionType?.duration || ""}
                    disabled
                    name={'duration'}
                    placeholder={t("Duration")}
                  />
                </FormItem>
              )}
            </div>
          </div>

          {(() => {
            const locationField = (
              <FormItem className={styles.input_field}>
                <label className={styles.sr_only}>{t("Location")}</label>
                <TextInput placeholder={t("Location")} {...register("location")} />
              </FormItem>
            );

            if (allowRecurring && watchRepeat) {
              return (
                <>
                  <div className={styles.sub_row}>
                    <RecurrenceSelector compact />
                  </div>
                  <div className={styles.sub_row}>
                    <div className={styles.sub_row_item_half}>
                      {locationField}
                    </div>
                  </div>
                </>
              );
            }

            return (
              <div className={styles.sub_row}>
                {allowRecurring && <RecurrenceSelector compact />}
                <div style={{ flex: 1 }}>
                  {locationField}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Rate / Payment row */}
      <div className={styles.icon_row}>
        <img src={DollarIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          {/* Row 1: Rate + Payment Method */}
          <div className={styles.split_row}>
            <FormItem className={styles.input_field}>
              <label className={styles.sr_only}>{t("Rate")}</label>
              <div className={styles.currency_container}>
                <span>
                  { isEdit ?
                    generateCurrencySymbol(event?.rateCurrency) || coachProfile?.currencySymbol :
                    coachProfile?.currencySymbol }
                </span>
                <input
                  {...register("rate", { onChange: onRateChange })}
                  placeholder="0"
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  readOnly={isEdit && !paymentEditable}
                />
              </div>
            </FormItem>

            {(!isEdit || paymentEditable) && (
              <PaymentSelector
                t={t}
                control={control}
                watchPaymentMethod={watchPaymentMethod}
                selectedAthletes={selectedAthletes}
                allowsPayments={stripeEnabled}
                defaultPaymentMethod={sessionDefaultPaymentMethod}
              />
            )}
            {isEdit && !paymentEditable && (
              <PaymentSelector
                t={t}
                control={control}
                watchPaymentMethod={watchPaymentMethod}
                selectedAthletes={selectedAthletes}
                allowsPayments={stripeEnabled}
                defaultPaymentMethod={sessionDefaultPaymentMethod}
                isDisabled
              />
            )}
          </div>

          <PaymentFields
            selectedAthletes={selectedAthletes}
            availablePackages={availablePackages}
            paymentEditable={paymentEditable}
            isEdit={isEdit}
            watchPaymentMethod={watchPaymentMethod}
            watchPayingAthlete={watchPayingAthlete}
            isCheckout={isCheckout}
            shouldShowPayingAthlete={shouldShowPayingAthlete}
            cardOnFileStatus={cardOnFileStatus}
            isLoadingPackages={isLoadingPackages}
          />
        </div>
      </div>

      {/* Notes row */}
      <div className={styles.icon_row}>
        <img src={EditPenIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          <FormItem className={`${styles.input_field} ${styles.notes_field}`}>
            <label className={styles.sr_only} htmlFor="session-notes">{t("Notes")}</label>
            <AutoGrowTextarea
              id="session-notes"
              {...register("notes")}
              watchedValue={watchNotes}
              placeholder={t("Add note here...")}
            />
          </FormItem>
        </div>
      </div>

      {/* CN-2808: private note row — facility personnel only (coaches, admins, schedulers) */}
      <div className={styles.icon_row}>
        <img src={LockIcon} alt="" className={styles.row_icon} />
        <div className={styles.row_content}>
          {showPrivateNote ? (
            <FormItem className={`${styles.input_field} ${styles.notes_field} ${styles.private_notes_field}`}>
              <div className={styles.private_notes_header}>
                <label htmlFor="session-private-notes">{t("Private note")}</label>
                <span className={styles.private_notes_badge}>{t("Facility personnel only")}</span>
              </div>
              <AutoGrowTextarea
                id="session-private-notes"
                {...register("privateNotes")}
                watchedValue={watchPrivateNotes}
                autoFocus={privateNoteOpened && !watchPrivateNotes}
                placeholder={t("Add a private note...")}
                aria-describedby="session-private-notes-hint"
                data-testid="session-private-notes-textarea"
              />
              <span id="session-private-notes-hint" className={styles.sr_only}>
                {t("Athletes won't see this note or get it in their emails.")}
              </span>
            </FormItem>
          ) : (
            <button
              type="button"
              className={styles.add_private_note}
              onClick={() => setPrivateNoteOpened(true)}
              data-testid="session-add-private-note"
            >
              + {t("Add private note")}
            </button>
          )}
        </div>
      </div>

      {/* CN-2798: on by default; a fresh form each time it opens resets it. */}
      <Controller
        name="notifyStudents"
        control={control}
        defaultValue={true}
        render={({ field }) => (
          <NotifyStudentsToggle
            checked={field.value}
            onChange={field.onChange}
            subject={isEdit ? "change" : "booking"}
          />
        )}
      />
    </div>
  );
}
